/**
 * KAN-834: установка приложения менеджера на главный экран.
 * Android (Chrome): событие beforeinstallprompt ловится сразу при старте
 * (captureInstallPrompt в main.tsx, оно может прийти раньше страницы входа)
 * и хранится здесь до нажатия кнопки. iPhone такого события не даёт:
 * показывается текст "Поделиться, затем На экран Домой".
 * KAN-845: Android во встроенном браузере (Telegram, WebView) тоже не даёт
 * события и не умеет ставить приложение: просим открыть ссылку в Chrome.
 */

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    notify();
  });
  target.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export function getInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Показать системное окно установки; true, если пользователь согласился. */
export async function promptInstall(): Promise<boolean> {
  const event = deferred;
  if (!event) return false;
  await event.prompt();
  const choice = await event.userChoice;
  deferred = null;
  notify();
  return choice.outcome === 'accepted';
}

/** iPhone, iPad (iPadOS представляется как Mac с сенсорным экраном). */
export function isIosDevice(userAgent: string, maxTouchPoints: number): boolean {
  return /iPhone|iPad|iPod/i.test(userAgent) || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

/** Android во встроенном браузере приложения: Telegram или WebView ("; wv)"). */
export function isAndroidInAppBrowser(userAgent: string): boolean {
  if (!/Android/i.test(userAgent)) return false;
  return /Telegram/i.test(userAgent) || /; wv\)/.test(userAgent);
}

/** Приложение уже открыто с главного экрана. */
export function isStandaloneDisplay(win: Window = window): boolean {
  const iosStandalone = (win.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || !!win.matchMedia?.('(display-mode: standalone)')?.matches;
}

/** Какую подсказку установки показать. */
export type InstallHintKind = 'installed' | 'prompt' | 'ios' | 'inapp' | 'manual';

export function installHintKind(opts: {
  standalone: boolean;
  canPrompt: boolean;
  ios: boolean;
  /** Android во встроенном браузере (isAndroidInAppBrowser). */
  inApp?: boolean;
}): InstallHintKind {
  if (opts.standalone) return 'installed';
  if (opts.canPrompt) return 'prompt';
  if (opts.ios) return 'ios';
  return opts.inApp ? 'inapp' : 'manual';
}

/** Только для тестов: сбросить сохранённое событие. */
export function resetInstallPromptForTests(): void {
  deferred = null;
  listeners.clear();
}
